import useBodyScrollLock from '../hooks/useBodyScrollLock';
import React, { useState, useEffect, memo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import { RiArrowRightSLine, RiArrowLeftSLine, RiLockLine } from "react-icons/ri";
import useSwipeNavigation from '../hooks/useSwipeNavigation';

const FluidMediaModal = memo(({ show = false, onClose, mediaUrls = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isSmallImage, setIsSmallImage] = useState(false);

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? mediaUrls.length - 1 : prevIndex - 1));
    }, [mediaUrls.length]);

    const goToNext = useCallback(() => {
        setCurrentIndex((prevIndex) => (prevIndex === mediaUrls.length - 1 ? 0 : prevIndex + 1));
    }, [mediaUrls.length]);

    useBodyScrollLock(show);

    useEffect(() => {
        const handleKeys = (event) => {
            if (event.key === 'Escape') {
                event.stopImmediatePropagation();
                onClose();
                return;
            }
            if (event.key === 'ArrowLeft') goToPrevious();
            if (event.key === 'ArrowRight') goToNext();
        };

        const handleWheel = (event) => {
            if (event.ctrlKey || event.metaKey) {
                event.preventDefault();
            }
        };

        const handleGestureStart = (event) => { event.preventDefault(); };
        const handleGestureChange = (event) => { event.preventDefault(); };
        const handleGestureEnd = (event) => { event.preventDefault(); };

        document.addEventListener('keydown', handleKeys, { capture: true });
        document.addEventListener('wheel', handleWheel, { passive: false });
        document.addEventListener('gesturestart', handleGestureStart, { passive: false });
        document.addEventListener('gesturechange', handleGestureChange, { passive: false });
        document.addEventListener('gestureend', handleGestureEnd, { passive: false });

        return () => {
            document.removeEventListener('keydown', handleKeys, { capture: true });
            document.removeEventListener('wheel', handleWheel);
            document.removeEventListener('gesturestart', handleGestureStart);
            document.removeEventListener('gesturechange', handleGestureChange);
            document.removeEventListener('gestureend', handleGestureEnd);
        };
    }, [onClose, goToNext, goToPrevious]);

    useEffect(() => {
        if (mediaUrls.length > 1) {
            const preloadNext = new Image();
            const preloadPrev = new Image();

            const nextIndex = (currentIndex + 1) % mediaUrls.length;
            const prevIndex = (currentIndex - 1 + mediaUrls.length) % mediaUrls.length;

            preloadNext.src = mediaUrls[nextIndex].url;
            preloadPrev.src = mediaUrls[prevIndex].url;
        }
    }, [currentIndex, mediaUrls]);

    const currentMediaUrl = mediaUrls[currentIndex];

    const handleContextMenu = (e) => {
        e.preventDefault();
    };

    const { onTouchStart, onTouchEnd } = useSwipeNavigation({
        onSwipeLeft: goToNext,
        onSwipeRight: goToPrevious,
    });

    if (!show) return null;

    return createPortal(
        <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-80 no-zoom overscroll-contain"
             style={{ zIndex: 11000 }}
             onClick={onClose}
             onContextMenu={handleContextMenu}
             onTouchStart={onTouchStart}
             onTouchEnd={onTouchEnd}>
            <style>{`
    @layer utilities {
        .no-zoom {
            touch-action: none;
            user-select: none;
            -ms-touch-action: none;
            -webkit-user-select: none;
            -webkit-touch-callout: none;
            -webkit-text-size-adjust: none;
            zoom: 1;
        }
        .thumbnail {
            filter: blur(30px);
        }
        @supports (-webkit-touch-callout: none) {
            .thumbnail { filter: blur(30px); }
        }
        @media (min-width: 768px) {
            .no-zoom { zoom: 1; }
            /* En desktop la imagen se ve mucho más grande, así que 30px de blur
               se leen demasiado suaves: se escala el desenfoque con la pantalla. */
            .thumbnail { filter: blur(60px); }
        }
        @media (min-width: 1280px) {
            .thumbnail { filter: blur(80px); }
        }
        /* Rango de ancho de un iPhone (320px del SE hasta 430px del Pro Max):
           ahí el blur sube un 10% sobre los 30px base. Va después del bloque
           @supports para que también aplique en Safari iOS. */
        @media (max-width: 430px) {
            .thumbnail { filter: blur(33px); }
        }
    }
`}</style>

            {mediaUrls.length > 1 && (
                <div
                    className="absolute left-1/2 transform -translate-x-1/2 bg-black bg-opacity-30 text-white px-4 py-2 rounded-full shadow-sm backdrop-blur-sm z-10 m-2"
                    style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
                >
                    <span className="font-medium">
                        {currentIndex + 1} / {mediaUrls.length}
                    </span>
                </div>
            )}

            <div className="relative w-full h-full max-w-4xl flex flex-col justify-center items-center p-4"
                 onClick={(e) => e.stopPropagation()}>
                <div className="w-full h-full flex justify-center items-center"
                     onContextMenu={handleContextMenu}>
                    <div className="relative w-full h-full sm:max-w-full sm:max-h-dvh sm:rounded-xl flex justify-center items-center">
                        {currentMediaUrl.file_format === 'video' ? (
                            <ReactPlayer
                                url={currentMediaUrl.url}
                                controls={true}
                                playing={show}
                                width="100%"
                                height="100%"
                                className="w-full h-full"
                                config={{
                                    file: {
                                        attributes: {
                                            controlsList: 'nodownload',
                                            preload: 'metadata',
                                        },
                                    },
                                }}
                            />
                        ) : (
                            <>
                                <img
                                    src={currentMediaUrl.url}
                                    alt={`Media ${currentIndex}`}
                                    className={`max-w-full max-h-full object-contain sm:rounded-xl ${currentIndex !== 0 ? 'thumbnail' : ''} ${isSmallImage ? 'w-full h-full' : ''}`}
                                    loading="lazy"
                                    onLoad={(e) => {
                                        const img = e.target;
                                        if (img.naturalWidth < 600 && img.naturalHeight < 600) {
                                            setIsSmallImage(true);
                                        } else {
                                            setIsSmallImage(false);
                                        }
                                    }}
                                    onContextMenu={handleContextMenu}
                                />
                            </>
                        )}
                        {currentIndex !== 0 && (
                            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-30 text-white px-4 py-2 rounded-full shadow-sm backdrop-blur-sm z-10 m-2">
                                <span className="font-medium">
                                  {currentMediaUrl.file_format}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {currentIndex >= 1 && (
                <div className="absolute inset-0 flex flex-col justify-center items-center">
                    <RiLockLine className="h-12 w-12 text-white mb-2" />
                    <span className="text-white font-medium">
                        To unlock the pack you must buy it
                    </span>
                </div>
            )}

            <button className="absolute right-4 m-2 bg-black bg-opacity-30 text-white p-2 rounded-full z-10"
                    style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
                    onClick={(event) => {
                        // Sin esto el click también le llega al overlay de atrás (mismo
                        // onClick={onClose}) por bubbling, y como onClose ahora hace un
                        // history.goBack() real (no un simple setState), dispararlo dos
                        // veces de un solo click salta DOS entradas de historial en vez
                        // de una — cerrando también el modal de perfil de abajo.
                        event.stopPropagation();
                        onClose();
                    }}>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            {mediaUrls.length > 1 && (
                <>
                    <button className="absolute top-1/2 transform -translate-y-1/2 m-1 bg-black bg-opacity-30 text-white p-1 rounded-full z-10"
                            style={{ left: 'calc(1rem + env(safe-area-inset-left, 0px))' }}
                            onClick={(event) => { goToPrevious(); event.stopPropagation(); }}>
                        <RiArrowLeftSLine className="h-6 w-6 md:h-14 md:w-14" />
                    </button>
                    <button className="absolute top-1/2 transform -translate-y-1/2 m-1 bg-black bg-opacity-30 text-white p-1 rounded-full z-10"
                            style={{ right: 'calc(1rem + env(safe-area-inset-right, 0px))' }}
                            onClick={(event) => { goToNext(); event.stopPropagation(); }}>
                        <RiArrowRightSLine className="h-6 w-6 md:h-14 md:w-14" />
                    </button>
                </>
            )}
        </div>,
        document.body
    );
});

FluidMediaModal.propTypes = {
    show: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    mediaUrls: PropTypes.arrayOf(PropTypes.shape({
        url: PropTypes.string.isRequired,
        file_format: PropTypes.string.isRequired,
    })),
};

export default FluidMediaModal;
